import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  ExternalLink,
  Calendar as CalendarIcon,
  MapPin,
  Building2,
  Star,
  Share2,
  Link2,
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase, ogImageUrlFor, trackingUrlFor } from '../supabase';
import { CryptoEvent, CATEGORY_BY_VALUE, rowToEvent } from '../types';

interface Props {
  eventId: string;
  onBack: () => void;
}

const setMeta = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (id: string, data: Record<string, unknown>) => {
  let el = document.head.querySelector<HTMLScriptElement>(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-jsonld', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const EventDetailPage: React.FC<Props> = ({ eventId, onBack }) => {
  const [event, setEvent] = useState<CryptoEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      const ev = rowToEvent(data);
      setEvent(ev);
      setLoading(false);

      // Fire-and-forget view ping (server-side via Edge Function would be cleaner;
      // browser-side insert is gated by service-role-only RLS, so this is a no-op
      // for MVP. The /event/:id rendered HTML will be the canonical view source.)
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  // SEO + OG tags ----------------------------------------------------------
  useEffect(() => {
    if (!event) return;
    const url = `${window.location.origin}/event/${event.id}`;
    const title = `${event.title} | What's On | onsolana.link`;
    const description =
      (event.description?.slice(0, 160) ?? `Solana ${event.category} event on ${format(event.date, 'MMM d, yyyy')}`).trim();
    const image = event.ogImageUrl ?? ogImageUrlFor(event.id);

    document.title = title;
    setMeta('description', description);
    setLink('canonical', url);

    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:image', image, 'property');
    setMeta('og:url', url, 'property');
    setMeta('og:type', 'event', 'property');
    setMeta('og:site_name', "What's On", 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);

    setJsonLd('event', {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: event.title,
      description: event.description ?? undefined,
      startDate: event.date.toISOString(),
      endDate: event.endDate?.toISOString(),
      location: event.location
        ? { '@type': 'Place', name: event.location }
        : undefined,
      organizer: event.companyName
        ? { '@type': 'Organization', name: event.companyName }
        : undefined,
      image,
      url,
    });

    return () => {
      document.title = "What's On | onsolana.link";
    };
  }, [event]);

  const handleShare = async () => {
    if (!event) return;
    const url = `${window.location.origin}/event/${event.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, url });
        return;
      } catch { /* fall through */ }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f3ecfc]">
        <div className="w-12 h-12 border-4 border-[#9945ff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f3ecfc] p-4">
        <div className="sticker bg-[#e9dffa] max-w-md w-full p-8 text-center">
          <h1 className="font-bold text-3xl uppercase tracking-tight text-[#0b0b0c] mb-2">
            Event not found
          </h1>
          <p className="text-sm text-[#0b0b0c]/65 mb-5">
            This event may have been removed, or the link is incorrect.
          </p>
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to calendar
          </button>
        </div>
      </div>
    );
  }

  const cat = CATEGORY_BY_VALUE[event.category];
  const trackedUrl = trackingUrlFor(event.linkSlug, {
    utm_source: 'calendar',
    utm_medium: 'event_page',
  });

  return (
    <div className="min-h-screen bg-[#f3ecfc] flex flex-col">
      <nav className="bg-[#9945ff] border-b-[1.5px] border-[#0b0b0c] px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1.5 px-3 h-11 sm:h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-[#0b0b0c] hover:bg-[#0b0b0c] hover:text-white transition-colors text-[11px] font-bold uppercase tracking-wider"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Calendar
          </button>
          <img src="/logo.svg" alt="What's On" className="h-10 sm:h-14 w-auto ml-2" />
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-3 sm:px-6 py-8 sm:py-16 flex-1 w-full">
        <article className="sticker bg-white p-4 sm:p-10">
          {/* Category & status pills */}
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span
              className="px-2.5 py-1 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider"
              style={{
                backgroundColor: cat.fill,
                color: cat.textColor === 'white' ? '#fff' : '#0b0b0c',
              }}
            >
              {cat.label}
            </span>
            {event.chain && (
              <span className="px-2.5 py-1 pill-outline text-[10px] font-bold uppercase tracking-wider">
                {event.chain === 'General Crypto' ? 'Industry-wide' : event.chain}
              </span>
            )}
            {event.featured && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 pill-outline text-[10px] font-bold uppercase tracking-wider">
                <Star className="w-3 h-3 fill-[#ffd24a] text-[#0b0b0c]" />
                Featured
              </span>
            )}
            {event.status !== 'approved' && (
              <span className="px-2.5 py-1 pill-outline text-[10px] font-bold uppercase tracking-wider capitalize">
                {event.status}
              </span>
            )}
          </div>

          <h1 className="font-bold text-3xl sm:text-6xl uppercase tracking-tight text-[#0b0b0c] leading-[0.95] mb-6">
            {event.title}
          </h1>

          {(event.companyName || event.location || event.time) && (
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-[#0b0b0c]/75 mb-6">
              <span className="inline-flex items-center gap-1.5">
                <CalendarIcon className="w-4 h-4 text-[#0b0b0c]/55" />
                {format(event.date, 'EEEE, MMM d, yyyy')}
                {event.time && ` · ${event.time}`}
              </span>
              {event.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-[#0b0b0c]/55" />
                  {event.location}
                </span>
              )}
              {event.companyName && (
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-[#0b0b0c]/55" />
                  {event.companyName}
                </span>
              )}
            </div>
          )}

          {event.imageUrl && (
            <img
              src={event.imageUrl}
              alt={event.title}
              loading="lazy"
              decoding="async"
              className="w-full sticker-soft mb-6"
            />
          )}

          {event.description && (
            <div className="prose prose-sm max-w-none text-[#0b0b0c]/85 leading-relaxed whitespace-pre-wrap mb-8">
              {event.description}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            {event.link && (
              <a
                href={trackedUrl ?? event.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 h-12 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors inline-flex items-center justify-center gap-2"
              >
                Visit event site
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <button
              onClick={handleShare}
              className="flex-1 h-12 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors inline-flex items-center justify-center gap-2"
            >
              {copied ? <Link2 className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? 'Link copied' : 'Share'}
            </button>
          </div>
        </article>
      </main>
    </div>
  );
};
